/**
 * Password strength levels
 */
export enum PasswordStrength {
  None = 0,
  Weak = 1,
  Medium = 2,
  Strong = 3,
  VeryStrong = 4
}

/**
 * Requirements for password validation
 */
export interface PasswordRequirements {
  minLength: number;
  requiresLowercase: boolean;
  requiresUppercase: boolean;
  requiresNumber: boolean;
  requiresSpecial: boolean;
}

/**
 * Result of a password validation check
 */
export interface PasswordValidationResult {
  strength: PasswordStrength;
  isValid: boolean;
  feedback: string[];
  color: string;
}

/**
 * Default password requirements
 */
export const defaultRequirements: PasswordRequirements = {
  minLength: 8,
  requiresLowercase: true,
  requiresUppercase: true,
  requiresNumber: true,
  requiresSpecial: true
};

/**
 * Validates a password against the specified requirements
 * @param password - The password to validate
 * @param requirements - The requirements to validate against
 * @returns A PasswordValidationResult object
 */
export function validatePassword(
  password: string,
  requirements: PasswordRequirements = defaultRequirements
): PasswordValidationResult {
  // Initialize feedback array
  const feedback: string[] = [];
  
  // Check length
  const hasMinLength = password.length >= requirements.minLength;
  if (!hasMinLength) {
    feedback.push(`Must be at least ${requirements.minLength} characters`);
  }
  
  // Check for lowercase letters
  const hasLowercase = requirements.requiresLowercase ? /[a-z]/.test(password) : true;
  if (requirements.requiresLowercase && !hasLowercase) {
    feedback.push('Must include a lowercase letter');
  }
  
  // Check for uppercase letters
  const hasUppercase = requirements.requiresUppercase ? /[A-Z]/.test(password) : true;
  if (requirements.requiresUppercase && !hasUppercase) {
    feedback.push('Must include an uppercase letter');
  }
  
  // Check for numbers
  const hasNumber = requirements.requiresNumber ? /\d/.test(password) : true;
  if (requirements.requiresNumber && !hasNumber) {
    feedback.push('Must include a number');
  }
  
  // Check for special characters
  const hasSpecial = requirements.requiresSpecial ? /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password) : true;
  if (requirements.requiresSpecial && !hasSpecial) {
    feedback.push('Must include a special character');
  }
  
  // Calculate strength
  let strength = PasswordStrength.None;
  
  if (password.length === 0) {
    strength = PasswordStrength.None;
  } else if (password.length < requirements.minLength) {
    strength = PasswordStrength.Weak;
  } else {
    // Count how many requirements are met
    const requirementsMet = [
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasSpecial
    ].filter(Boolean).length;
    
    // Set strength based on requirements met
    if (requirementsMet === 1) {
      strength = PasswordStrength.Weak;
    } else if (requirementsMet === 2) {
      strength = PasswordStrength.Medium;
    } else if (requirementsMet === 3) {
      strength = PasswordStrength.Strong;
    } else if (requirementsMet === 4) {
      strength = PasswordStrength.VeryStrong;
    }
  }
  
  // Determine if password is valid (meets all requirements)
  const isValid = 
    hasMinLength && 
    hasLowercase && 
    hasUppercase && 
    hasNumber && 
    hasSpecial;
  
  // Determine color based on strength
  let color = '';
  switch (strength) {
    case PasswordStrength.None:
      color = 'transparent';
      break;
    case PasswordStrength.Weak:
      color = 'var(--destructive)';
      break;
    case PasswordStrength.Medium:
      color = '#FFA500'; // Orange
      break;
    case PasswordStrength.Strong:
      color = '#2E8B57'; // Sea Green
      break;
    case PasswordStrength.VeryStrong:
      color = '#228B22'; // Forest Green
      break;
  }
  
  return {
    strength,
    isValid,
    feedback,
    color
  };
}

/**
 * Gets a text label for a password strength
 * @param strength - The password strength
 * @returns A text label for the password strength
 */
export function getPasswordStrengthLabel(strength: PasswordStrength): string {
  switch (strength) {
    case PasswordStrength.None:
      return 'No Password';
    case PasswordStrength.Weak:
      return 'Weak';
    case PasswordStrength.Medium:
      return 'Medium';
    case PasswordStrength.Strong:
      return 'Strong';
    case PasswordStrength.VeryStrong:
      return 'Very Strong';
    default:
      return '';
  }
} 